import React, { useRef, useMemo, useEffect, useState, useCallback } from 'react'
import dagre from 'dagre'
import {
  Recipe,
  DAGTask,
  DAG,
} from '@pollination-solutions/pollination-sdk'
import RecipeTaskNode from '~/components/nodes/RecipeTaskNode'
import RecipeInputNode from '~/components/nodes/RecipeInputNode'
import RecipeOutputNode from '~/components/nodes/RecipeOutputNode'
import RecipeEdge from '~/components/edges/RecipeEdge'
import RecipeHoverEdge from '~/components/edges/RecipeHoverEdge'
import RecipeCluster from '~/components/nodes/RecipeClusterNode'
import { locate } from '~/utils'
import { Graph } from '~/types'
import { TaskInput } from '~/types/queenbee/io'
import { isArtifact } from '~/utils/verify'

export interface RecipeGraphProps {
  recipe: Recipe
  position: [number, number]
  config: Graph.Config
}

export const RecipeGraph: React.FunctionComponent<RecipeGraphProps> = ({ recipe, position, config }) => {
  const [tx, ty] = position
  const dagRef = useRef<SVGSVGElement>(null)

  // Extract recipe flows
  const { main, subFlows } = useMemo(() => locate.recipeEntrypoint(recipe.flow), [recipe])

  const drawFlow = useCallback(
    (flow: DAG, graph: dagre.graphlib.Graph): void => {
      const { name: root, tasks, outputs, inputs } = flow

      graph.setNode(flow.name, { label: flow.name, nodeType: 'cluster', parentDag: flow.name })

      graph.setNode(`${flow.name}-tasks`, { label: `${flow.name}-tasks`, nodeType: 'cluster', parentDag: flow.name })
      graph.setParent(`${flow.name}-tasks`, flow.name)

      graph.setNode(`${flow.name}-inputs`, { label: `${flow.name}-inputs`, nodeType: 'cluster', parentDag: flow.name })
      graph.setParent(`${flow.name}-inputs`, flow.name)

      graph.setNode(`${flow.name}-out`, { label: `${flow.name}-out`, nodeType: 'cluster', parentDag: flow.name })
      graph.setParent(`${flow.name}-out`, flow.name)

      const checkIfSubFlow = (task: DAGTask): boolean => {
        return subFlows.find((sub) => sub.name === task.template) !== undefined
      }

      // Create main task nodes
      tasks.forEach((task) => {
        const isLoop = (task.loop ?? null) !== null
        const isSubFlow = checkIfSubFlow(task)

        const key = `${root}-${task.name}`

        // Task represents a sub-flow, do not draw as one item.
        if (isSubFlow) {
          const parent = subFlows.find((sf) => sf.name === task.template)
          if (!parent) {
            return
          }

          const link = (input: TaskInput): void => {
            if (input.from.type?.includes("Input")) {
              graph.setEdge(`main-${(input.from as any).variable}`, `${task.template}-${input.name}`)
            }

            if (input.from.type?.includes("Task")) {
              const sourceTask = (input.from as any).name
              const sourceDag = locate.parentDagByTaskName([main, ...subFlows], sourceTask)
              if (sourceDag) {
                graph.setEdge(`${sourceDag.name}-${sourceTask}`, `${task.template}-${input.name}`)

              }
            }
          }

          // Link parent input parameters
          task.arguments?.forEach((param) => {
            link(param)
          })

          return
        }

        graph.setNode(key, {
          label: task.name,
          width: isLoop ? 190 : 175,
          height: 44,
          nodeType: 'task',
          parentDag: flow.name,
        })

        graph.setParent(key, flow.name)
        graph.setParent(key, `${flow.name}-tasks`)

        task.needs?.forEach((dep) => {
          const depTask = tasks.find((t) => t.name === dep)
          if (!depTask) {
            return
          }
          const depIsSubFlow = checkIfSubFlow(depTask)
          if (depIsSubFlow) {
            const subFlow = subFlows.find((sf) => sf.name === depTask.template)

            task.arguments?.filter((p) => isArtifact(p)).forEach((art) => {
              const sourceLocation: string = (art.from as any).path

              subFlow?.outputs?.filter((subArt) => isArtifact(subArt)).forEach((subArt) => {
                const dest: string = (subArt.from as any).path

                const matches = sourceLocation.includes(dest)

                // Sub-flow output is a source for this task's argument
                if (matches) {
                  graph.setEdge(`${subFlow.name}-${subArt.name}`, `${flow.name}-${task.name}`)
                }
              })
            })
            return
          }

          graph.setEdge(root + '-' + dep, key)
        })
      })

      graph.setParent(`${root}-inputs`, flow.name)

      // Create input parameter nodes
      inputs?.forEach((param) => {
        const key = `${root}-${param.name}`
        graph.setNode(key, {
          label: param.name,
          width: 56,
          height: 46,
          nodeType: 'input',
          parentDag: flow.name,
          rank: 'same_ip',
        })

        graph.setParent(key, flow.name)
        graph.setParent(key, `${root}-inputs`)

        // Find and link tasks that reference this parameter
        tasks.forEach((task) => {
          const isSubFlow = checkIfSubFlow(task)
          if (isSubFlow) {
            return
          }

          task.arguments?.forEach((p) => {
            if (!p.from.type?.includes("Inputs")) {
              return
            }

            const recipeInputName = (p.from as any).variable

            if (recipeInputName === param.name) {
              graph.setEdge(key, root + '-' + task.name)
            }
          })
        })
      })

      // Create input artifact nodes
      inputs?.filter((i) => isArtifact(i))?.forEach((artifact) => {
        const key = `${root}-${artifact.name}`
        graph.setNode(key, {
          label: artifact.name,
          width: 56,
          height: 46,
          nodeType: 'input',
          parentDag: flow.name,
          rank: 'same_ia',
        })

        graph.setParent(key, flow.name)
        graph.setParent(key, `${root}-inputs`)

        // Find and link tasks that reference this artifact
        tasks.forEach((task) => {
          const isSubFlow = checkIfSubFlow(task)
          if (isSubFlow) {
            return
          }

          task.arguments?.filter((a) => isArtifact(a)).forEach((a) => {
            if ((a.from as any)?.type !== 'inputs') {
              return
            }

            const recipeInputName = (a.from as any).variable

            if (recipeInputName === artifact.name) {
              graph.setEdge(key, root + '-' + task.name)
            }
          })
        })
      })

      // Create output parameter nodes
      outputs?.filter((o) => !isArtifact(o)).forEach((param) => {
        const key = `${root}-${param.name}`
        graph.setNode(key, {
          label: param.name,
          width: 56,
          height: 46,
          nodeType: 'output',
          outputType: 'parameter',
          parentDag: flow.name,
        })

        graph.setParent(key, flow.name)
        graph.setParent(key, `${root}-out`)

        const { name } = param.from

        // Find and link the task that creates this parameter
        const match = tasks.find((task) => task.name === name)

        if (!match || checkIfSubFlow(match)) {
          return
        }

        graph.setEdge(root + '-' + match.name, key)
      })

      // Create output artifact nodes
      outputs?.filter((o) => isArtifact(o)).forEach((artifact) => {
        const key = `${root}-${artifact.name}`
        graph.setNode(key, {
          label: artifact.name,
          width: 56,
          height: 46,
          nodeType: 'output',
          outputType: artifact.from.type,
          parentDag: flow.name,
        })

        graph.setParent(key, flow.name)
        graph.setParent(key, `${root}-out`)

        switch (artifact.from.type as 'tasks' | 'folder') {
          case 'tasks': {
            // Find and link the named task
            const { name } = artifact.from

            const match = tasks.find((task) => task.name === name)

            if (match && !checkIfSubFlow(match)) {
              graph.setEdge(root + '-' + match.name, key)
            }

            break
          }
          case 'folder': {
            // Find any tasks that populate this output folder
            const test: string = (artifact.from as any).path ?? ''

            tasks.forEach((task) => {
              if (checkIfSubFlow(task)) {
                return
              }

              const { returns } = task

              const outputMatch = returns?.filter((o) => isArtifact(o)).find((out) => {
                // @ts-ignore
                const path = out.path?.split('/')
                const key = path?.slice(0, path.length - 1).join('')

                return key === test
              })

              const subFolderMatch = task.sub_folder?.includes(test)

              if (outputMatch || subFolderMatch) {
                graph.setEdge(root + '-' + task.name, key)
              }
            })

            break
          }
        }
      })
    },
    [main, subFlows]
  )

  const dag = useMemo(() => {
    const graph = new dagre.graphlib.Graph({ compound: true })
    graph.setGraph({ rankdir: config.dir, ranksep: 50, nodesep: config.dir === 'LR' ? 35 : 60 })
    graph.setDefaultEdgeLabel(() => {
      return {}
    })

    drawFlow(main, graph)

    subFlows.forEach((sub) => {
      drawFlow(sub, graph)
      graph.setParent(sub.name, main.name)
    })

    dagre.layout(graph)

    return graph
  }, [main, subFlows, drawFlow, config])

  const edges = useMemo(() => {
    return dag.edges().map((e, i) => {
      const edge = dag.edge(e)
      return <RecipeEdge edge={edge} key={`edge-${i}`} />
    })
  }, [dag])

  const overlay = useMemo(() => {
    return dag.edges().map((e, i) => {
      const edge = dag.edge(e)
      return <RecipeHoverEdge edge={edge} key={`edge-${i}`} />
    })
  }, [dag])

  const nodes = useMemo(() => {
    return dag.nodes().map((n, i) => {
      const node = dag.node(n)
      if (!node) {
        console.log(`Missing node: ${n}`)
        return null
      }
      const label = node.label ?? ''
      const parent = recipe.flow.find((f) => f.name === (node as any)?.parentDag ?? '')
      if (!parent) {
        return null
      }
      const { x, y, width, height } = node
      switch ((node as any).nodeType) {
        case 'task': {
          const task = parent.tasks.find((t) => t.name === label)
          if (!task) {
            return null
          }
          return <RecipeTaskNode position={[x, y]} label={label} task={task} key={`node-${n}-${i}`} />
        }
        case 'input': {
          const input = locate.flowInputByName(parent, label)
          if (!input) {
            console.log(label)
            return null
          }
          return <RecipeInputNode position={[x, y]} label={label} input={input} />
        }
        case 'output':
          return (
            <RecipeOutputNode position={[x, y]} label={label} type={(node as any).outputType} key={`node-${n}-${i}`} />
          )
        case 'cluster':
          // Do not draw border for main or layout-coercing clusters
          if (['main', 'inputs', 'out', 'tasks'].find((meta) => label.includes(meta))) {
            return null
          }
          return <RecipeCluster position={[x, y]} dimensions={[width, height]} label={label} />
        default:
          return null
      }
    })
  }, [dag, recipe.flow])

  const [[cx, cy], setCenter] = useState<[number, number]>([0, 0])
  const [ready, setReady] = useState(false)

  // Center graph on screen once on load
  useEffect(() => {
    if (ready) {
      return
    }

    const w = dagRef.current?.clientWidth ?? 0
    const h = dagRef.current?.parentElement?.parentElement?.clientHeight ?? 0
    const dagWidth = dag.graph().width ?? 0
    const dagHeight = dag.graph().height ?? 0

    setCenter([0.5 * (w - dagWidth), 0.5 * (h - dagHeight)])
    setReady(true)
  }, [dag, ready])

  return (
    <svg className="recipe-svg" ref={dagRef}>
      {ready ? (
        <g transform={`translate(${cx + tx}, ${cy + ty})`}>
          {edges}
          {nodes}
          {overlay}
        </g>
      ) : null}
      <style jsx>{`
        .recipe-svg {
          width: 100%;
          height: 100%;
          z-index: 5;
        }
      `}</style>
    </svg>
  )
}

export default RecipeGraph
